import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { atTimeOfDay } from '@/pages/PersonCard/cardDates';
import { type } from '@/theme/typography';

import type { Autosave } from './useAutosave';

/**
 * §6.6's **save indicator — "the most important feedback on the screen"**, and the reassurance ADR 0007
 * offers *instead* of a Save button (*"a Save button teaches people that not pressing it loses work,
 * which is the anxiety autosave was built to remove"*).
 *
 * It renders §6.6's four states and nothing else:
 *
 * | State | Rendering |
 * |---|---|
 * | Saved | `ink-500` · *Saved 14:32*, the time in mono |
 * | Saving | `ink-500` · spinner · *Saving…* |
 * | Failed | `negative` · *Not saved — retrying* + **Retry** |
 * | Offline | `negative` · *"Offline. Your answers are kept in this tab until the connection returns."* + **Retry** (BUG-047) |
 *
 * ⚠️ **The time is the response's `updated_at` — the server's own save timestamp — and never this
 * machine's clock.** A clock-side time would be a claim about when the request left rather than about
 * what was written, and the two differ exactly when it matters: a slow save, a clock that is wrong, a
 * request answered by a server that wrote something older. `autosave.test.tsx` drives them apart (the
 * fake clock is years away from the payload) so the assertion can only pass one way.
 *
 * ⚠️ **Nothing here says *Save*, and nothing here is a control except **Retry**.** The `/save/i` sweep
 * over every control on this surface is in `fillingIn.test.tsx`, and *Saved* is a report rather than an
 * offer — the one word this screen may say about saving.
 *
 * The three wide states — the 409s, the 422 and the router block — belong to `SaveNotice` below, because
 * a paragraph does not fit in a header cell and none of them is an indicator.
 */
export function SaveIndicator({ autosave }: { autosave: Autosave }) {
  const { t, i18n } = useTranslation('interviews');
  const { status } = autosave;

  if (status.kind === 'clean' || status.kind === 'stopped' || status.kind === 'refused')
    return null;

  if (status.kind === 'saving') {
    return (
      /*
       * `aria-live="polite"` and not `role="status"`'s louder cousins: this is a report somebody glances
       * at, and a screen reader must not interrupt a sentence being dictated to announce it. The spinner
       * is `aria-hidden` — the word beside it is the announcement.
       */
      <p className={cn(type.metadata, 'flex items-center gap-1.5')} aria-live="polite">
        <Loader2 size={12} strokeWidth={1.5} className="animate-spin" aria-hidden />
        {t('filling.save.saving')}
      </p>
    );
  }

  if (status.kind === 'saved') {
    return (
      <p className={cn(type.metadata, type.numeral)} aria-live="polite">
        {/*
         * The `dateTime` carries the instant the sentence is about, so the fact is machine-readable as
         * well as legible — the shape §6.5's dates and the blinded state already use.
         */}
        <time dateTime={status.at}>
          {t('filling.save.saved', { time: atTimeOfDay(status.at, i18n.language) })}
        </time>
      </p>
    );
  }

  if (status.kind === 'offline') {
    /*
     * ⚠️ **This sentence promises something, so it has to be true** — and it is §6.6's own wording,
     * kept because the shared §8 banner's version promised a queue nothing performed (BUG-017). Here the
     * next attempt is made by `useAutosave` on the `online` event and on a bounded backoff, and
     * `autosave.test.tsx` drives a dropped connection and a restored one to prove it.
     *
     * ⚠️ **The Retry is here since BUG-047, and it is §8's rule rather than a second thought.** §6.6's
     * table gives the control to the *Failed* row alone; §8 requires that no state be a dead end, and
     * this is the state a reader sits in longest. It is also the one case the automatic ladder cannot
     * cover on its own: a connection restored behind a captive portal or a proxy fires no `online`
     * event, so *"until the connection returns"* would otherwise be kept only at the ladder's own
     * pace. Pressing it goes out at once, exactly as it does beside *Not saved — retrying*; if the
     * connection really is still away it fails locally and this line stays.
     */
    return (
      <div className="flex items-center gap-2">
        <p className={cn(type.metadata, 'max-w-xs text-negative')} role="alert">
          {t('filling.save.offline')}
        </p>
        <Button variant="ghost" size="sm" onClick={autosave.retryNow}>
          {t('filling.save.retry')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <p className={cn(type.metadata, 'text-negative')} role="alert">
        {t('filling.save.failed')}
      </p>
      {/*
       * ⚠️ **The one retry that goes out immediately.** Every automatic attempt waits at least
       * `AUTOSAVE_RETRY_BACKOFF_MS[0]`, because a 429 must not be amplified by the client that caused it
       * (`queryClient.ts`, BUG-005/BUG-011) — a person pressing a button is not that client.
       */}
      <Button variant="ghost" size="sm" onClick={autosave.retryNow}>
        {t('filling.save.retry')}
      </Button>
    </div>
  );
}

/**
 * **The three things that stop being an indicator and become a sentence: the autosave's two `409`s, its
 * `422`, and leaving the page with something at risk.**
 *
 * ⚠️ **Neither may lose what is on screen, and that is the whole design of this block.** A 409 means the
 * record stopped accepting changes — it was submitted in another tab (ADR 0008) or the candidate's
 * personal data was erased on request (ADR 0009, protection #6) — and in both cases the honest answer is
 * *"this cannot be saved, and everything you have typed is still here"*. So the form is left exactly as
 * it is, no field is cleared, no navigation happens on its own, and the one next step offered is a
 * **re-read** of the interview: for a submitted one that lands on §6.7, which is where the record now
 * lives.
 *
 * ⚠️ **The stopped block returns before the leave prompt below, and that is only sound because a 409 no
 * longer arms the block** (BUG-026). It did until 2026-08-03: `stopped` was in `useAutosave`'s `atRisk`,
 * this branch returned first, and the *Leave anyway* button — the only control in the product that calls
 * `blocker.proceed()` — was therefore never rendered while the blocker was armed, so every in-app link on
 * the screen did nothing. Two states, one slot, and the wide one wins; what changed is that after a 409
 * there is nothing to block, because the record has stopped accepting changes. **If a future state ever
 * needs both at once, render them both** — the prompt cannot be the branch that loses.
 *
 * ⚠️ **It says which refusal in its own words, never the server's `detail` and never a code** (§8, §11).
 * The sentence is built in two parts because the API answers both 409s with one shape (see `AutosaveRefusal` in
 * `useAutosave.ts`): the lead is true of every refusal and is on screen at once, and the second line
 * names *which* as soon as the one probe read answers. If it never answers, the screen keeps the true
 * half and claims nothing further — a wrong explanation here would send somebody to look for a
 * submission that never happened, or tell them a candidate's data was erased when it was not.
 */
export function SaveNotice({ autosave }: { autosave: Autosave }) {
  const { t } = useTranslation('interviews');
  const { status, blocker } = autosave;

  /**
   * ⚠️ **The `422` (BUG-068), and it is a block of its own rather than the one below with a third
   * sentence.** `stopped`'s lead says *"this interview has stopped accepting changes"*, which is what a
   * 409 means and is not what a 422 means: the record accepts changes, **this body** does not. Saying
   * otherwise sends the reader to look for a submission in another tab or an erasure — the wrong-cause
   * mistake BUG-041 already cost this screen. What the two blocks share is the clause neither may drop:
   * everything typed is still on the page.
   *
   * ⚠️ **There is no Retry here, and its absence is the fix.** The same body is refused identically for
   * ever, so a control offering to send it again is a promise nothing can keep — BUG-017's shape, which
   * this state carried until 2026-08-09 in the words *"Not saved — retrying"*. The way out is an edit,
   * which the second sentence asks for and `useAutosave` performs: the write path is **not** latched
   * shut on a 422, so the next change to the form is saved like any other.
   */
  const refused =
    status.kind === 'refused' ? (
      <div className="mt-4 rounded border border-negative bg-negative-wash p-4" role="alert">
        <p className={type.bodyPrimary}>{t('filling.save.refused.lead')}</p>
        <p className={cn(type.body, 'mt-2')}>{t('filling.save.refused.kept')}</p>
      </div>
    ) : null;

  if (status.kind === 'stopped') {
    return (
      <div
        className="mt-4 rounded border border-negative bg-negative-wash p-4"
        /*
         * `role="alert"` here and not on the indicator: this one is a change of what the screen can do,
         * and a reader who is typing needs to be told at once rather than on their next glance.
         */
        role="alert"
      >
        <p className={type.bodyPrimary}>{t('filling.save.stopped.lead')}</p>
        {status.refusal !== 'unknown' && (
          <p className={cn(type.body, 'mt-2')}>{t(`filling.save.stopped.${status.refusal}`)}</p>
        )}
        <Button className="mt-3" variant="secondary" size="sm" onClick={autosave.reread}>
          {t('filling.save.stopped.reread')}
        </Button>
      </div>
    );
  }

  /**
   * ⚠️ **With everything saved this is `idle` and there is no prompt at all** — deliberately, and
   * `autosave.test.tsx` asserts the absence. An *"unsaved changes"* dialog on a screen that saves every
   * two seconds is the anxiety ADR 0007 removed, reintroduced at the exit. What is blocked is only the
   * case where leaving really loses something: see `useAutosave`'s `atRisk`.
   */
  if (blocker.state !== 'blocked') return refused;

  /*
   * ⚠️ **Both, when both apply — the prompt is never the branch that loses** (the rule the block above
   * records for BUG-026). `refused` is not one of `atRisk`'s states, so the two are not meant to coexist;
   * they can all the same, because a blocker already `blocked` by a failed save stays blocked while the
   * retry that follows answers 422. Rendering them as alternatives would put the wide block in the slot
   * and take *Leave anyway* — the one control in the product that calls `blocker.proceed()` — off the
   * screen, which is exactly how every in-app link on this page once stopped working.
   */
  return (
    <>
      {refused}
      <div className="mt-4 rounded border border-negative bg-negative-wash p-4" role="alert">
        <p className={type.bodyPrimary}>{t('filling.save.leave.warning')}</p>
        <div className="mt-3 flex items-center gap-2">
          {/* The safe action first and the losing one second, which is §4's order for a `danger` pair. */}
          <Button variant="secondary" size="sm" onClick={() => blocker.reset?.()}>
            {t('filling.save.leave.stay')}
          </Button>
          <Button variant="danger" size="sm" onClick={() => blocker.proceed?.()}>
            {t('filling.save.leave.leave')}
          </Button>
        </div>
      </div>
    </>
  );
}
